import React, { useState, Fragment } from 'react';
import { Button } from '../components/Button';
import { ValueProposition } from '../components/ValueProposition';
import { PricingPlans } from '../components/PricingPlans';
import { AddOns } from '../components/AddOns';
import { AdvancedAutomation } from '../components/AdvancedAutomation';
import { StarIcon, QuoteIcon, ArrowRightIcon, ChevronDownIcon } from 'lucide-react';
function ServiceHero() {
  return <section className="w-full bg-[#0F0F0F] py-20 md:py-32" data-id="element-3192">
      <div className="max-w-7xl mx-auto px-8 md:px-12 text-center" data-id="element-3193">
        <h1 className="font-['Poppins'] font-bold text-5xl md:text-7xl lg:text-8xl text-[#F5F5F5] mb-8 leading-tight" data-id="element-3194">
          Australia's Leading
          <br data-id="element-3195" />
          <span className="text-[#F1B530]" data-id="element-3196">AI Answering Service</span>
        </h1>

        <p className="font-['Inter'] text-xl md:text-2xl text-[#A0A0A0] max-w-4xl mx-auto mb-12 leading-relaxed font-light" data-id="element-3197">
          Professional call answering, 24/7 availability, and seamless
          integration. Trusted by thousands of Australian businesses.
        </p>

        <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12" data-id="element-3198">
          <Button size="lg" data-id="element-3199">Start Free 7-Day Trial</Button>
          <Button variant="secondary" size="lg" data-id="element-3200">
            1300 XXX XXX
          </Button>
        </div>

        <div className="flex items-center justify-center gap-2 text-[#F1B530]" data-id="element-3201">
          {[...Array(5)].map((_, i) => <StarIcon key={i} className="w-6 h-6 fill-current" data-id="element-3202" />)}
          <span className="font-['Inter'] text-[#A0A0A0] ml-2" data-id="element-3203">
            4.9/5 from 500+ reviews
          </span>
        </div>
      </div>
    </section>;
}
function ProcessSteps() {
  const steps = [{
    number: '01',
    title: 'Sign Up',
    description: 'Create your account and customize your AI receptionist in minutes'
  }, {
    number: '02',
    title: 'Configure',
    description: 'Set your greeting, FAQs, and routing rules to match your business'
  }, {
    number: '03',
    title: 'Go Live',
    description: 'Forward your calls and let AI handle everything 24/7'
  }, {
    number: '04',
    title: 'Grow',
    description: 'Monitor performance, capture leads, and scale effortlessly'
  }];
  return <section className="w-full bg-[#0F0F0F] py-28" data-id="element-3204">
      <div className="max-w-7xl mx-auto px-8 md:px-12" data-id="element-3205">
        <div className="text-center mb-20" data-id="element-3206">
          <h2 className="font-['Poppins'] font-bold text-5xl md:text-6xl text-[#F5F5F5] mb-6" data-id="element-3207">
            How It Works
          </h2>
          <p className="font-['Inter'] text-xl text-[#A0A0A0] font-light" data-id="element-3208">
            Get started in 4 simple steps
          </p>
        </div>

        <div className="hidden lg:flex items-stretch gap-4" data-id="element-3209">
          {steps.map((step, index) => <Fragment key={index} data-id="element-3210">
              <div className="flex-1 bg-[#252525] border border-[#2A2A2A] rounded-2xl p-8 hover:border-[#F1B530]/30 transition-all duration-300 flex flex-col" data-id="element-3211">
                <div className="font-['Poppins'] text-6xl font-bold text-[#F1B530] mb-6 opacity-50" data-id="element-3212">
                  {step.number}
                </div>
                <h3 className="font-['Inter'] font-bold text-2xl text-[#F5F5F5] mb-4" data-id="element-3213">
                  {step.title}
                </h3>
                <p className="font-['Inter'] text-[#A0A0A0] leading-relaxed font-light" data-id="element-3214">
                  {step.description}
                </p>
              </div>

              {index < steps.length - 1 && <div className="flex items-center justify-center px-2" data-id="element-3215">
                  <ArrowRightIcon className="w-6 h-6 text-[#F1B530]" strokeWidth={2} data-id="element-3216" />
                </div>}
            </Fragment>)}
        </div>

        {/* Mobile/Tablet Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:hidden" data-id="element-3217">
          {steps.map((step, index) => <div key={index} className="bg-[#252525] border border-[#2A2A2A] rounded-2xl p-8 hover:border-[#F1B530]/30 transition-all duration-300 flex flex-col" data-id="element-3218">
              <div className="font-['Poppins'] text-6xl font-bold text-[#F1B530] mb-6 opacity-50" data-id="element-3219">
                {step.number}
              </div>
              <h3 className="font-['Inter'] font-bold text-2xl text-[#F5F5F5] mb-4" data-id="element-3220">
                {step.title}
              </h3>
              <p className="font-['Inter'] text-[#A0A0A0] leading-relaxed font-light" data-id="element-3221">
                {step.description}
              </p>
            </div>)}
        </div>
      </div>
    </section>;
}
function IndustryBenefits() {
  const industries = [{
    name: 'Healthcare',
    benefits: ['HIPAA compliant', 'Appointment scheduling', 'Emergency routing', 'Patient messaging']
  }, {
    name: 'Legal',
    benefits: ['Client intake', 'Confidential handling', 'Urgent call screening', 'Case management']
  }, {
    name: 'Real Estate',
    benefits: ['Property inquiries', 'Viewing bookings', 'Lead capture', 'Tenant support']
  }, {
    name: 'Trade Services',
    benefits: ['Job booking', 'Emergency calls', 'Quote requests', 'Service scheduling']
  }, {
    name: 'Retail',
    benefits: ['Order taking', 'Store inquiries', 'Stock checks', 'Customer support']
  }, {
    name: 'Professional Services',
    benefits: ['Client screening', 'Appointment booking', 'Message handling', 'Consultation scheduling']
  }];
  return <section className="w-full bg-[#1A1A1A] py-28" data-id="element-3222">
      <div className="max-w-7xl mx-auto px-8 md:px-12" data-id="element-3223">
        <div className="text-center mb-16" data-id="element-3224">
          <h2 className="font-['Poppins'] font-bold text-5xl md:text-6xl text-[#F5F5F5] mb-6" data-id="element-3225">
            Trusted Across Industries
          </h2>
          <p className="font-['Inter'] text-xl text-[#A0A0A0] font-light" data-id="element-3226">
            Tailored solutions for every business type
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-3227">
          {industries.map((industry, index) => <div key={index} className="bg-[#252525] border border-[#2A2A2A] rounded-2xl p-8 hover:border-[#F1B530]/30 transition-all duration-300" data-id="element-3228">
              <h3 className="font-['Poppins'] font-bold text-2xl text-[#F5F5F5] mb-6" data-id="element-3229">
                {industry.name}
              </h3>
              <ul className="space-y-3" data-id="element-3230">
                {industry.benefits.map((benefit, idx) => <li key={idx} className="font-['Inter'] text-[#A0A0A0] flex items-start" data-id="element-3231">
                    <span className="text-[#F1B530] mr-2" data-id="element-3232">•</span>
                    {benefit}
                  </li>)}
              </ul>
            </div>)}
        </div>
      </div>
    </section>;
}
function Testimonials() {
  const testimonials = [{
    quote: "Since switching to this AI answering service, we haven't missed a single lead. The 24/7 coverage is a game-changer for our business.",
    author: 'Sarah Mitchell',
    role: 'Director, Mitchell & Associates Law',
    rating: 5
  }, {
    quote: 'The appointment booking feature alone has saved us 10+ hours per week. Our clients love the instant response time.',
    author: 'James Chen',
    role: 'Practice Manager, Sydney Dental Care',
    rating: 5
  }, {
    quote: 'Professional, reliable, and cost-effective. This service pays for itself in captured opportunities. Highly recommended.',
    author: 'Emma Thompson',
    role: 'Owner, Thompson Real Estate',
    rating: 5
  }];
  return <section className="w-full bg-[#0F0F0F] py-28" data-id="element-3233">
      <div className="max-w-7xl mx-auto px-8 md:px-12" data-id="element-3234">
        <div className="text-center mb-16" data-id="element-3235">
          <h2 className="font-['Poppins'] font-bold text-5xl md:text-6xl text-[#F5F5F5] mb-6" data-id="element-3236">
            What Our Clients Say
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-3237">
          {testimonials.map((testimonial, index) => <div key={index} className="bg-[#252525] border border-[#2A2A2A] rounded-2xl p-8 relative" data-id="element-3238">
              <QuoteIcon className="w-12 h-12 text-[#F1B530]/20 mb-6" data-id="element-3239" />

              <div className="flex gap-1 mb-6" data-id="element-3240">
                {[...Array(testimonial.rating)].map((_, i) => <StarIcon key={i} className="w-5 h-5 text-[#F1B530] fill-current" data-id="element-3241" />)}
              </div>

              <p className="font-['Inter'] text-[#F5F5F5] mb-6 leading-relaxed font-light" data-id="element-3242">
                "{testimonial.quote}"
              </p>

              <div data-id="element-3243">
                <div className="font-['Inter'] font-semibold text-[#F5F5F5]" data-id="element-3244">
                  {testimonial.author}
                </div>
                <div className="font-['Inter'] text-sm text-[#A0A0A0]" data-id="element-3245">
                  {testimonial.role}
                </div>
              </div>
            </div>)}
        </div>
      </div>
    </section>;
}
function FAQ() {
  const [openIndex, setOpenIndex] = useState<number | null>(0);
  const faqs = [{
    question: 'How does the AI answering service work?',
    answer: 'Our AI receptionist answers calls 24/7, captures caller information, handles FAQs, and can even book appointments. It integrates seamlessly with your existing systems and can be customized to match your business needs.'
  }, {
    question: 'Can I customize the greeting and responses?',
    answer: 'Absolutely! You can fully customize your company greeting, upload FAQs, set routing rules, and define your brand voice. The AI learns from your business to provide accurate, on-brand responses.'
  }, {
    question: "What happens if the AI can't handle a call?",
    answer: 'The AI can transfer calls to your team with full context when needed. You set the rules for when calls should be escalated, ensuring important matters always reach the right person.'
  }, {
    question: 'How quickly can I get started?',
    answer: "Setup takes less than 5 minutes. Simply sign up, customize your greeting and settings, forward your number, and you're live. We provide step-by-step guidance throughout the process."
  }, {
    question: 'Is there a contract or commitment?',
    answer: 'No long-term contracts required. Start with our 7-day free trial, then choose a monthly plan that fits your needs. You can upgrade, downgrade, or cancel anytime.'
  }];
  return <section className="w-full bg-[#1A1A1A] py-28 md:py-36" data-id="element-3246">
      <div className="max-w-4xl mx-auto px-8 md:px-12" data-id="element-3247">
        <div className="text-center mb-16" data-id="element-3248">
          <h2 className="font-['Poppins'] font-bold text-4xl md:text-5xl text-[#F5F5F5] mb-6" data-id="element-3249">
            Frequently Asked Questions
          </h2>
          <p className="font-['Inter'] text-lg text-[#A0A0A0] font-light" data-id="element-3250">
            Everything you need to know about our AI answering service
          </p>
        </div>

        <div className="space-y-4" data-id="element-3251">
          {faqs.map((faq, index) => <div key={index} className="bg-[#252525] border border-[#2A2A2A] rounded-2xl overflow-hidden transition-all duration-300 hover:border-[#F1B530]/30" data-id="element-3252">
              <button onClick={() => setOpenIndex(openIndex === index ? null : index)} className="w-full px-8 py-6 flex items-center justify-between text-left" data-id="element-3253">
                <span className="font-['Inter'] font-semibold text-xl text-[#F5F5F5] pr-8" data-id="element-3254">
                  {faq.question}
                </span>
                <ChevronDownIcon className={`w-6 h-6 text-[#F1B530] flex-shrink-0 transition-transform duration-300 ${openIndex === index ? 'rotate-180' : ''}`} data-id="element-3255" />
              </button>

              <div className={`overflow-hidden transition-all duration-300 ${openIndex === index ? 'max-h-96' : 'max-h-0'}`} data-id="element-3256">
                <div className="px-8 pb-6" data-id="element-3257">
                  <p className="font-['Inter'] text-[#A0A0A0] leading-relaxed font-light" data-id="element-3258">
                    {faq.answer}
                  </p>
                </div>
              </div>
            </div>)}
        </div>
      </div>
    </section>;
}
export function PhoneServicesInspiredBase() {
  return <div className="w-full min-h-screen bg-[#0F0F0F]" data-id="element-3259">
      <ServiceHero data-id="element-3260" />
      <ValueProposition data-id="element-3261" />
      <ProcessSteps data-id="element-3262" />
      <IndustryBenefits data-id="element-3263" />
      <PricingPlans data-id="element-3264" />
      <Testimonials data-id="element-3265" />
      <AddOns data-id="element-3266" />
      <AdvancedAutomation data-id="element-3267" />
      <FAQ data-id="element-3268" />
    </div>;
}